import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SettingsShell } from "./SettingsShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
    ["owner", "Owner"], ["admin", "Admin"], ["executive", "Executive"],
    ["development_manager", "Development Manager"], ["construction_manager", "Construction Manager"],
    ["project_manager", "Project Manager"], ["superintendent", "Superintendent"],
    ["estimator", "Estimator"], ["accountant", "Accountant"], ["office_admin", "Office Admin"],
    ["sales_rep", "Sales Rep"], ["acquisitions_manager", "Acquisitions Manager"],
    ["investor_relations", "Investor Relations"], ["read_only", "Read-only"],
];

export default function UsersSettings() {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [invite, setInvite] = useState({ email: "", role_key: "project_manager" });
    const [devToken, setDevToken] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/organizations/current/members");
            setMembers(data);
        } catch (err) {
            toast.error("Could not load members");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const sendInvite = async (e) => {
        e.preventDefault();
        try {
            const { data } = await api.post("/organizations/current/invite", invite);
            toast.success(`Invited ${invite.email}`);
            if (data.dev_invite_token) setDevToken(data.dev_invite_token);
            setInvite({ email: "", role_key: "project_manager" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invite failed");
        }
    };

    const updateMember = async (uid, patch) => {
        try {
            await api.patch(`/organizations/current/members/${uid}`, patch);
            toast.success("Updated");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    const extraTabs = user?.is_super_admin ? [{ to: "/app/settings/software-branding", label: "Software Branding" }] : [];

    return (
        <SettingsShell title="Users & Roles" subtitle="Invite teammates and set their role in this organization." extraTabs={extraTabs}>
            <div className="flex items-center justify-between mb-6">
                <div className="text-sm text-muted-foreground">{members.length} member{members.length === 1 ? "" : "s"}</div>
                <button onClick={() => setOpen(true)} className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="invite-open">Invite user</button>
            </div>

            <div className="border border-border" data-testid="members-table">
                <div className="grid grid-cols-5 px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
                    <div className="col-span-2">User</div>
                    <div>Role</div>
                    <div>Status</div>
                    <div className="text-right">Joined</div>
                </div>
                {loading && <div className="p-8 text-sm text-muted-foreground">Loading…</div>}
                {!loading && members.length === 0 && <div className="p-8 text-sm text-muted-foreground">No members yet.</div>}
                {members.map((m) => (
                    <div key={m.user_id} className="grid grid-cols-5 px-4 py-4 border-b border-border last:border-b-0 items-center text-sm" data-testid={`member-row-${m.user_id}`}>
                        <div className="col-span-2">
                            <div className="font-medium">{m.first_name} {m.last_name}</div>
                            <div className="text-xs text-muted-foreground">{m.email}</div>
                        </div>
                        <div>
                            <select
                                value={m.role_key}
                                onChange={(e) => updateMember(m.user_id, { role_key: e.target.value })}
                                className="border border-border bg-background px-2 py-1 text-xs"
                                data-testid={`member-role-${m.user_id}`}
                            >
                                {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                            </select>
                        </div>
                        <div>
                            <button
                                onClick={() => updateMember(m.user_id, { is_active: !m.is_active })}
                                className={`px-2 py-1 text-xs border ${m.is_active ? "border-border" : "border-destructive text-destructive"}`}
                                data-testid={`member-toggle-${m.user_id}`}
                            >
                                {m.is_active ? "Active" : "Deactivated"}
                            </button>
                        </div>
                        <div className="text-xs text-muted-foreground text-right">{m.joined_at ? new Date(m.joined_at).toLocaleDateString() : "—"}</div>
                    </div>
                ))}
            </div>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent data-testid="invite-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">Invite a teammate</DialogTitle>
                        <DialogDescription>They'll receive an invite token to join this organization.</DialogDescription>
                    </DialogHeader>
                    {devToken ? (
                        <div className="border border-border p-4 space-y-3">
                            <div className="overline">Dev invite token</div>
                            <div className="font-mono text-xs break-all p-2 bg-secondary" data-testid="dev-invite-token">{devToken}</div>
                            <div className="text-xs text-muted-foreground">Share this URL with them: <span className="font-mono">/app/accept-invite?token={devToken}</span></div>
                            <button onClick={() => { setDevToken(null); setOpen(false); load(); }} className="border border-border px-4 py-2 text-sm">Done</button>
                        </div>
                    ) : (
                        <form onSubmit={sendInvite} className="space-y-4">
                            <div>
                                <Label>Email</Label>
                                <Input type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} required className="mt-2" data-testid="invite-email" />
                            </div>
                            <div>
                                <Label>Role</Label>
                                <select value={invite.role_key} onChange={(e) => setInvite({ ...invite, role_key: e.target.value })} className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm" data-testid="invite-role">
                                    {ROLES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                </select>
                            </div>
                            <DialogFooter>
                                <button type="button" onClick={() => setOpen(false)} className="border border-border px-4 py-2 text-sm">Cancel</button>
                                <button type="submit" className="bg-foreground text-background px-4 py-2 text-sm hover:opacity-90" data-testid="invite-send">Send invite</button>
                            </DialogFooter>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
        </SettingsShell>
    );
}
